import React from 'react';

/**
 * AnalysisProgress - Display real-time analysis progress
 *
 * Shows a progress bar, status messages, and phase indicators during transcript analysis.
 */
const AnalysisProgress = ({ progress, error, onRetry, phases }) => {
  if (!progress && !error) {
    return null;
  }

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'connecting':
        return '...';
      case 'starting':
      case 'reading':
        return '...';
      case 'initializing':
        return '...';
      case 'analyzing':
        return '...';
      case 'saving':
        return '...';
      case 'complete':
        return null;
      case 'partial':
        return '!';
      case 'failed':
        return '!';
      case 'cancelled':
        return '!';
      default:
        return '...';
    }
  };

  const isComplete = progress?.stage === 'complete';
  const isPartial = progress?.stage === 'partial';
  const isFailed = progress?.stage === 'failed';
  const isCancelled = progress?.stage === 'cancelled';

  return (
    <div className={`analysis-progress ${error || isFailed ? 'has-error' : ''} ${isComplete ? 'is-complete' : ''} ${isPartial ? 'is-partial' : ''}`}>
      {error ? (
        <div className="progress-error">
          <div className="error-icon">!</div>
          <div className="error-content">
            <h4>Analysis Failed</h4>
            <p>{error}</p>
            {onRetry && (
              <button className="btn btn-secondary" onClick={onRetry}>
                Try Again
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="progress-header">
            {isComplete ? (
              <span className="progress-icon complete">&#x2713;</span>
            ) : isPartial ? (
              <span className="progress-icon partial">!</span>
            ) : isFailed ? (
              <span className="progress-icon failed">!</span>
            ) : isCancelled ? (
              <span className="progress-icon cancelled">&#x2715;</span>
            ) : (
              <span className="progress-icon spinning">{getStageIcon(progress?.stage)}</span>
            )}
            <span className="progress-message">{progress?.message || 'Processing...'}</span>
          </div>

          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progress?.percentage || 0}%` }}
            />
          </div>

          <div className="progress-percentage">{progress?.percentage || 0}%</div>

          {/* Phase indicators */}
          {phases && (
            <div className="phase-indicators">
              {Object.entries(phases).map(([key, phase]) => (
                <div key={key} className={`phase-indicator ${phase.status}`}>
                  <span className="phase-icon">
                    {phase.status === 'complete' && <span className="check">&#x2713;</span>}
                    {phase.status === 'in_progress' && <span className="spinner">...</span>}
                    {phase.status === 'pending' && <span className="pending">&#x25CB;</span>}
                    {phase.status === 'error' && <span className="error">!</span>}
                  </span>
                  <span className="phase-name">{phase.display}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <style>{`
        .analysis-progress {
          background: var(--card-bg, #2a2a3e);
          border-radius: 12px;
          padding: 24px;
          margin: 16px 0;
        }

        .analysis-progress.has-error {
          border: 1px solid var(--error-color, #f44336);
        }

        .analysis-progress.is-complete {
          border: 1px solid var(--success-color, #4caf50);
        }

        .progress-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .progress-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: bold;
        }

        .progress-icon.spinning {
          background: var(--primary-color, #6366f1);
          color: white;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .progress-icon.complete {
          background: var(--success-color, #4caf50);
          color: white;
        }

        .progress-icon.cancelled {
          background: var(--warning-color, #ff9800);
          color: white;
        }

        .progress-icon.partial {
          background: var(--warning-color, #ff9800);
          color: white;
        }

        .progress-icon.failed {
          background: var(--error-color, #f44336);
          color: white;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(0.95); }
        }

        .progress-message {
          font-size: 16px;
          color: var(--text-color, #fff);
          flex: 1;
        }

        .progress-bar-container {
          height: 8px;
          background: var(--bg-tertiary, #1a1a2e);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--primary-color, #6366f1), var(--primary-light, #818cf8));
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .is-complete .progress-bar-fill {
          background: var(--success-color, #4caf50);
        }

        .progress-percentage {
          font-size: 12px;
          color: var(--text-muted, #888);
          text-align: right;
        }

        .progress-error {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .error-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--error-color, #f44336);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .error-content h4 {
          margin: 0 0 8px 0;
          color: var(--error-color, #f44336);
        }

        .error-content p {
          margin: 0 0 16px 0;
          color: var(--text-muted, #888);
        }

        /* Phase indicators */
        .phase-indicators {
          display: flex;
          justify-content: space-between;
          margin-top: 16px;
          gap: 8px;
        }

        .phase-indicator {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 8px;
          background: var(--bg-tertiary, #1a1a2e);
          font-size: 12px;
          transition: all 0.3s ease;
        }

        .phase-indicator .phase-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .phase-indicator.pending .phase-icon {
          background: var(--bg-secondary, #2a2a3e);
          color: var(--text-muted, #888);
        }

        .phase-indicator.in_progress .phase-icon {
          background: var(--primary-color, #6366f1);
          color: white;
          animation: pulseSoft 1.5s ease-in-out infinite;
        }

        .phase-indicator.complete .phase-icon {
          background: var(--success-color, #4caf50);
          color: white;
        }

        .phase-indicator.error .phase-icon {
          background: var(--warning-color, #ff9800);
          color: white;
        }

        @keyframes pulseSoft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        .phase-indicator.pending {
          opacity: 0.5;
        }

        .phase-indicator.in_progress {
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.3);
        }

        .phase-indicator.complete {
          background: rgba(76, 175, 80, 0.1);
          border: 1px solid rgba(76, 175, 80, 0.3);
        }

        .phase-indicator.error {
          background: rgba(255, 152, 0, 0.1);
          border: 1px solid rgba(255, 152, 0, 0.3);
        }

        .phase-indicator .phase-name {
          color: var(--text-color, #fff);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .phase-indicator.pending .phase-name {
          color: var(--text-muted, #888);
        }

        /* Responsive: stack phases on small screens */
        @media (max-width: 600px) {
          .phase-indicators {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalysisProgress;
